import React, { useEffect, useState } from "react";
import { Coins, Gift, TrendingUp, ShoppingBag, Clock } from "lucide-react";
import { useGlobal } from "../../context/GlobalContext";
import { images } from "../../assets/images";
import { formatDateMMDDYYYY } from "../../utils/timezone";

const PointHistory = () => {
  const { getPointHistory } = useGlobal();
  const [pointHistory, setPointHistory] = useState({});
  const [activeTab, setActiveTab] = useState("earning");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getPointHistory();
        setPointHistory(data);
      } catch (error) {
        console.error("Error fetching point history:", error);
      }
    };

    fetchData();
  }, []);

  const formatDate = (date) => (date ? formatDateMMDDYYYY(date) : "-");

  const earningHistory = pointHistory?.earning_history || [];
  const recentRedemptions = pointHistory?.recent_redemptions || [];

  return (
    <div className="content-part2">
      <div className="row g-3 mb-4 ">
        <div className="col-4">
          <div className="p-3 bg-white rounded-4 shadow-sm border border-light d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 gap-md-3 h-100">
            <div
              className="rounded-circle text-primary d-flex align-items-center justify-content-center icon-box"
              style={{ background: "#0d6efd1a" }}
            >
              <TrendingUp size={20} className="icon-size" />
            </div>

            <div className="d-flex flex-column">
              <p className="text-color fs-12 mb-0 fw-semibold uppercase">
                Earned
              </p>
              <h5 className="fw-bold mb-0 fs-17">
                {pointHistory?.summary?.total_earned} Pts
              </h5>
            </div>
          </div>
        </div>
        <div className="col-4">
          <div className="p-3 bg-white rounded-4 shadow-sm border border-light d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 gap-md-3 h-auto">
            <div className="rounded-circle bg-success bg-opacity-10 text-success d-flex align-items-center justify-content-center icon-box">
              <Coins size={20} className="icon-size" />
            </div>

            <div className="d-flex flex-column">
              <p className="text-color fs-12 mb-0 fw-semibold uppercase">
                Current Balance
              </p>
              <h5 className="fw-bold text-success mb-0 fs-17">
                {pointHistory?.summary?.balance} Pts
              </h5>
            </div>
          </div>
        </div>
        <div className="col-4">
          <div className="p-3 bg-white rounded-4 shadow-sm border border-light d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 gap-md-3 h-auto">
            <div className="rounded-circle bg-danger bg-opacity-10 text-danger d-flex align-items-center justify-content-center icon-box">
              <ShoppingBag size={20} className="icon-size" />
            </div>

            <div className="d-flex flex-column">
              <p className="text-color fs-12 mb-0 fw-semibold uppercase">
                Total Redeemed
              </p>
              <h5 className="fw-bold text-dark fs-17 mb-0">
                {pointHistory?.summary?.total_redeemed} Pts
              </h5>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-only tab bar */}
      <div className="d-flex d-lg-none db-tabs mb-3">
        <button
          className={`db-tab ${activeTab === "earning" ? "active" : ""}`}
          onClick={() => setActiveTab("earning")}
        >
          <Clock size={18} />
          Earning History
        </button>
        <button
          className={`db-tab ${activeTab === "redemptions" ? "active" : ""}`}
          onClick={() => setActiveTab("redemptions")}
        >
          <Gift size={18} />
          Recent Redemptions
        </button>
      </div>

      <div className="row g-4">
        <div className={`col-lg-7 ${activeTab === "earning" ? "d-block" : "d-none d-lg-block"}`}>
          <div className="bg-white rounded-4 shadow-sm border border-light overflow-hidden">
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-light">
              <div className="d-flex align-items-center gap-2">
                <Clock size={18} className="text-primary" />
                <h5 className="mb-0 fw-bold text-dark fs-15">
                  Earning History
                </h5>
              </div>
            </div>
            <div
              className="p-0"
              style={{ maxHeight: "560px", overflowY: "auto" }}
            >
              {earningHistory.length === 0 ? (
                <div className="py-5 text-center text-muted fs-14">
                  No earnings yet
                </div>
              ) : (
                earningHistory.map((item) => (
                  <div
                    key={item.job_pid}
                    className="py-2 px-3 border-bottom hover-shadow transition-200"
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <h6 className="fw-bold text-dark fs-14 mb-1">
                        {item.services}
                      </h6>
                      <span className="text-success fw-extrabold fs-15">
                        +{item.total_points}
                      </span>
                    </div>
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <span className="text-muted fs-13">
                        #{item.job_pid?.toUpperCase()} • {formatDate(item.schedule_date)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className={`col-lg-5 ${activeTab === "redemptions" ? "d-block" : "d-none d-lg-block"}`}>
          <div className="bg-white rounded-4 shadow-sm border border-light overflow-hidden">
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-light">
              <div className="d-flex align-items-center gap-2">
                <Gift size={18} className="text-secondary" />
                <h5 className="mb-0 fw-bold text-dark fs-15">
                  Recent Redemptions
                </h5>
              </div>
            </div>
            <div
              className="p-2"
              style={{ maxHeight: "560px", overflowY: "auto" }}
            >
              {recentRedemptions.length === 0 ? (
                <div className="py-5 text-center text-muted fs-14">
                  No redemptions yet
                </div>
              ) : (
                recentRedemptions.map((item) => (
                  <div
                    key={item.id}
                    className="d-flex align-items-center gap-3 p-3 rounded-4 border border-light mb-3 bg-light bg-opacity-50"
                  >
                    <div
                      className="rounded-3 overflow-hidden shadow-sm"
                      style={{ width: "64px", height: "64px", flexShrink: 0 }}
                    >
                      <img
                        src={item.product_image || images.placeHolderImg}
                        alt={item.name}
                        className="w-100 h-100 object-fit-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = images.placeHolderImg;
                        }}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <h6 className="fw-bold text-dark fs-14 mb-0">
                          {item.product_name}
                        </h6>
                        <span className="text-danger fw-bold fs-12">
                          -{item.points_cost}
                        </span>
                      </div>
                      <div className="d-flex align-items-center justify-content-between flex-lg-wrap">
                        <p className="text-muted fs-13 mb-2">
                          #{item.product_pid} • {formatDate(item.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}

              <div className="mt-4 text-center">
                <p className="text-muted fs-13 px-4 italic">
                  Rewards are usually processed within 24-48 hours. Check your
                  email for confirmation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointHistory;
